/*
 * Copyright 2026 Sony Corporation
 */

import {
  SceneOpSchema,
  type LightType,
  type PrimitiveType,
  type SceneDoc,
  type SceneNode,
  type SceneOp,
  type Transform,
  type Vec3,
} from './schema';
import { DEFAULT_HDRI_RESOLUTION, DEFAULT_RESOLUTION } from '../services/polyhaven';

/**
 * The one place a document changes.
 *
 * Pure, and with no Babylon import: every op, every limit and every rejection is
 * testable without a renderer, which is most of the test surface in devplan §11.
 */

/**
 * Bounds on what one batch of ops can do (devplan §7.4 rule 4).
 *
 * They exist for the agent, which may decide to add ten thousand cubes, but
 * they apply to the GUI too — a limit only the AI is subject to is a limit
 * nobody has tested.
 */
export const LIMITS = {
  maxNodes: 500,
  maxLights: 32,
  /** How far from the origin anything may sit, in metres. */
  maxExtent: 1000,
  maxScale: 1000,
} as const;

const DEFAULT_SIZE: Record<PrimitiveType, number> = {
  box: 1,
  sphere: 1,
  cylinder: 1,
  plane: 4,
  ground: 20,
};

const DEFAULT_INTENSITY: Record<LightType, number> = {
  directional: 0.9,
  point: 40,
  spot: 40,
};

const LABELS: Record<PrimitiveType | LightType, string> = {
  box: 'Box',
  sphere: 'Sphere',
  cylinder: 'Cylinder',
  plane: 'Plane',
  ground: 'Ground',
  directional: 'Directional light',
  point: 'Point light',
  spot: 'Spot light',
};

const IDENTITY: Transform = { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] };

/** How high a shape's origin sits when it rests on the ground. */
const RESTING_HEIGHT: Record<PrimitiveType, (size: number) => number> = {
  box: (size) => size / 2,
  sphere: (size) => size / 2,
  cylinder: (size) => size / 2,
  plane: () => 0,
  ground: () => 0,
};

const clamp = (value: number, limit: number): number => Math.min(Math.max(value, -limit), limit);

const clampVec = (vec: Vec3, limit: number): Vec3 => [clamp(vec[0], limit), clamp(vec[1], limit), clamp(vec[2], limit)];

/**
 * Somewhere the new object can actually be seen: resting on the ground, and
 * beside whatever is already at that spot rather than inside it.
 */
const freeSpot = (doc: SceneDoc, height: number): Vec3 => {
  const taken = new Set(doc.nodes.map((node) => node.transform.position.join()));
  for (let x = 0; ; x += 1) {
    const candidate: Vec3 = [x, height, 0];
    if (!taken.has(candidate.join())) {
      return candidate;
    }
  }
};

/** Unique within the document, so the outliner reads as a list of things rather than of ids. */
const nameFor = (doc: SceneDoc, kind: PrimitiveType | LightType): string => {
  const label = LABELS[kind];
  const taken = new Set(doc.nodes.map((node) => node.name));
  for (let index = 1; ; index += 1) {
    const candidate = index === 1 ? label : `${label} ${index}`;
    if (!taken.has(candidate)) {
      return candidate;
    }
  }
};

const countLights = (doc: SceneDoc): number => doc.nodes.filter((node) => node.kind === 'light').length;

export interface ReduceOptions {
  /** Injected so tests can assert on ids; the app passes a random one. */
  createId?: () => string;
}

const randomId = (): string => `nd_${Math.random().toString(36).slice(2, 10)}`;

export interface ReduceResult {
  doc: SceneDoc;
  /** What was refused, in the order it was refused. Shown to the user, returned to the agent. */
  errors: string[];
}

/**
 * Applies a batch as one transaction against the document.
 *
 * A rejected op is skipped and reported rather than aborting the batch: a model
 * that gets one id wrong should still place the other twenty-three cameras, and
 * a user dragging two objects should not lose both because one hit a limit.
 */
export const applyOps = (doc: SceneDoc, ops: readonly SceneOp[], options: ReduceOptions = {}): ReduceResult => {
  const createId = options.createId ?? randomId;
  const errors: string[] = [];
  let next = doc;

  const add = (node: SceneNode): void => {
    if (next.nodes.length >= LIMITS.maxNodes) {
      errors.push(`A scene may hold at most ${LIMITS.maxNodes} objects.`);
      return;
    }
    if (node.kind === 'light' && countLights(next) >= LIMITS.maxLights) {
      errors.push(`A scene may hold at most ${LIMITS.maxLights} lights.`);
      return;
    }
    next = { ...next, nodes: [...next.nodes, node] };
  };

  for (const raw of ops) {
    // Parsed here and not only at the boundary: this is the check that holds
    // whether the op came from a button, a drag or a language model.
    const parsed = SceneOpSchema.safeParse(raw);
    if (!parsed.success) {
      errors.push(parsed.error.issues[0]?.message ?? 'Unsupported operation.');
      continue;
    }
    const op = parsed.data;

    switch (op.op) {
      case 'add_primitive': {
        const size = op.size ?? DEFAULT_SIZE[op.type];
        add({
          id: createId(),
          name: nameFor(next, op.type),
          kind: 'primitive',
          primitive: { type: op.type, size },
          transform: {
            ...IDENTITY,
            position: op.position
              ? clampVec(op.position, LIMITS.maxExtent)
              : freeSpot(next, RESTING_HEIGHT[op.type](size)),
          },
        });
        break;
      }

      case 'add_light': {
        add({
          id: createId(),
          name: nameFor(next, op.type),
          kind: 'light',
          light: { type: op.type, intensity: op.intensity ?? DEFAULT_INTENSITY[op.type] },
          transform: { ...IDENTITY, position: clampVec(op.position ?? [4, 6, 4], LIMITS.maxExtent) },
        });
        break;
      }

      case 'add_asset': {
        // Named after the catalogue entry where the caller knew it, because
        // "Wooden table" reads in an outliner and `wooden_table_02` does not.
        const label = op.name ?? op.slug;
        const taken = next.nodes.some((node) => node.name === label);
        add({
          id: createId(),
          name: taken ? `${label} ${next.nodes.length + 1}` : label,
          kind: 'asset',
          asset: {
            source: op.source,
            slug: op.slug,
            resolution: op.resolution ?? DEFAULT_RESOLUTION,
            ...(op.pinned_hash ? { pinned_hash: op.pinned_hash } : {}),
          },
          // On the ground and beside what is there, like a primitive; an asset's
          // own origin is usually its base, so no height offset.
          transform: {
            ...IDENTITY,
            position: op.position ? clampVec(op.position, LIMITS.maxExtent) : freeSpot(next, 0),
          },
        });
        break;
      }

      case 'set_environment': {
        if (op.slug === null) {
          next = { ...next, environment: undefined };
          break;
        }
        next = {
          ...next,
          environment: {
            hdri: {
              source: 'polyhaven',
              slug: op.slug,
              resolution: op.resolution ?? DEFAULT_HDRI_RESOLUTION,
              ...(op.pinned_hash ? { pinned_hash: op.pinned_hash } : {}),
            },
            intensity: op.intensity ?? next.environment?.intensity ?? 1,
            rotation_y: op.rotation_y ?? next.environment?.rotation_y ?? 0,
          },
        };
        break;
      }

      case 'set_material': {
        for (const id of op.node_ids) {
          const target = next.nodes.find((node) => node.id === id);
          if (!target) {
            errors.push(`No object with id ${id}.`);
            continue;
          }
          if (target.kind !== 'primitive') {
            errors.push(`${target.name} brings its own materials.`);
            continue;
          }
          const material =
            op.slug === null
              ? undefined
              : {
                  source: 'polyhaven' as const,
                  slug: op.slug,
                  resolution: op.resolution ?? DEFAULT_RESOLUTION,
                  ...(op.pinned_hash ? { pinned_hash: op.pinned_hash } : {}),
                };
          next = {
            ...next,
            nodes: next.nodes.map((node) => (node.id === id ? { ...node, material } : node)),
          };
        }
        break;
      }

      case 'set_transform': {
        const target = next.nodes.find((node) => node.id === op.node_id);
        if (!target) {
          errors.push(`No object with id ${op.node_id}.`);
          break;
        }
        const transform: Transform = {
          position: clampVec(op.position ?? target.transform.position, LIMITS.maxExtent),
          rotation: op.rotation ?? target.transform.rotation,
          scale: clampVec(op.scale ?? target.transform.scale, LIMITS.maxScale),
        };
        next = {
          ...next,
          nodes: next.nodes.map((node) => (node.id === op.node_id ? { ...node, transform } : node)),
        };
        break;
      }

      case 'duplicate': {
        for (const id of op.node_ids) {
          const source = next.nodes.find((node) => node.id === id);
          if (!source) {
            errors.push(`No object with id ${id}.`);
            continue;
          }
          const label =
            source.kind === 'primitive'
              ? nameFor(next, source.primitive.type)
              : source.kind === 'light'
                ? nameFor(next, source.light.type)
                : `${source.name} copy`;
          // Offset along x so the copy is visible rather than hidden inside the
          // original, which reads as "nothing happened".
          const [x, y, z] = source.transform.position;
          add({
            ...source,
            id: createId(),
            name: label,
            transform: { ...source.transform, position: clampVec([x + 1, y, z], LIMITS.maxExtent) },
          });
        }
        break;
      }

      case 'delete': {
        const removing = new Set(op.node_ids);
        for (const id of op.node_ids) {
          if (!next.nodes.some((node) => node.id === id)) {
            errors.push(`No object with id ${id}.`);
          }
        }
        next = { ...next, nodes: next.nodes.filter((node) => !removing.has(node.id)) };
        break;
      }
    }
  }

  return { doc: next, errors };
};

/**
 * What *New scene* opens on: a ground plane and a key light (devplan §5.1).
 *
 * The light is here rather than left to the editor's own because the editor's
 * lights are furniture, not scenery — a document saved without one builds a
 * `.babylon` that renders black on the Camera page. U2's HDRI environment is
 * what eventually makes it optional.
 */
export const emptyDoc = (options: ReduceOptions = {}): SceneDoc =>
  applyOps(
    { doc_version: 1, units: 'm', nodes: [] },
    [
      { op: 'add_primitive', type: 'ground' },
      { op: 'add_light', type: 'directional' },
    ],
    options
  ).doc;
